import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Alert, Button } from '@mui/material';
import { UserRole } from '../types/user';
import { getCurrentUser } from '../services/auth';
import {
  canRead, hasSiteEntry, hasGrant, SENSITIVE_SECTIONS, firstAllowedPortalPath,
} from '../config/sitePermissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallbackPath?: string;
  /** Section on the Permissions page that gates this page, if any. */
  section?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles, 
  fallbackPath = '/admin',
  section,
}) => {
  const [user, setUser] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [shouldRedirect, setShouldRedirect] = React.useState(false);

  React.useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Redirect if user clicked the redirect button
  if (shouldRedirect) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Permissions and Ports are the sensitive ones. They used to be
  // admin-only whatever the Permissions page said, so ticking write for a staff member
  // did nothing. Now the ticked section is what decides -- and it must be ticked.
  const sensitive = Boolean(section) && SENSITIVE_SECTIONS.includes(section as string);
  const grantedSensitive = sensitive && hasGrant(user, 'portal', section as string);

  // A page this person cannot open is not worth a notice: send them to the first page
  // they can use. Only someone with nothing at all sees a message, and it tells them
  // what to do about it.
  const elsewhere = firstAllowedPortalPath(user);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  if (!user || (!allowedRoles.includes(user.role) && !grantedSensitive)) {
    if (elsewhere && elsewhere !== currentPath) return <Navigate to={elsewhere} replace />;
    return <NoAccess />;
  }

  // Hiding the menu item is not enough on its own -- the address can still be
  // typed in. Someone never set up on the Permissions page keeps their old
  // access; once it has been set, what was set governs.
  const configured = hasSiteEntry(user, 'portal');
  const blocked = Boolean(section)
    && user.role !== 'admin'
    && (sensitive
      ? !grantedSensitive
      : configured && !canRead(user, 'portal', section as string));

  if (blocked) {
    if (elsewhere && elsewhere !== currentPath) return <Navigate to={elsewhere} replace />;
    return <NoAccess />;
  }

  return <>{children}</>;
};

/** Shown only when there is no page at all for this person to be sent to. */
const NoAccess = () => (
  <Box sx={{ p: 8, textAlign: 'center' }}>
    <Typography variant="h6" sx={{ mb: 1 }}>Nothing has been shared with you yet</Typography>
    <Typography variant="body2" color="text.secondary">
      Ask an administrator to grant your access on the Permissions page.
    </Typography>
  </Box>
);

export default ProtectedRoute;
